import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { parseNum, numStr } from "@/lib/format";

const INVALIDATE = ["conference", "conferences", "summary", "comparison", "history", "products", "price-alerts", "suppliers"];

const toPayloadItem = ([pid, d]) => ({
  product_id: pid,
  ...(d.initial_qty !== undefined && { initial_qty: parseNum(d.initial_qty) }),
  ...(d.purchases_qty !== undefined && { purchases_qty: parseNum(d.purchases_qty) }),
  ...(d.final_qty !== undefined && { final_qty: parseNum(d.final_qty) }),
  ...(d.counted !== undefined && { counted: d.counted }),
});

function withoutFields(drafts, pid, fields) {
  if (!drafts[pid]) return drafts;
  const rest = { ...drafts[pid] };
  fields.forEach((f) => delete rest[f]);
  const next = { ...drafts };
  if (Object.keys(rest).length) next[pid] = rest;
  else delete next[pid];
  return next;
}

function useConferenceMutations(conf, payload, dirty, clearDrafts, setPendingCard) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const key = ["conference", conf.id];
  const invalidate = useCallback(() => INVALIDATE.forEach((k) => qc.invalidateQueries({ queryKey: [k] })), [qc]);
  const done = (msg) => (data) => { qc.setQueryData(key, data); invalidate(); toast.success(msg); };
  const fail = (e) => toast.error(e.message);
  const put = () => api(`/conferences/${conf.id}`, { method: "PUT", body: payload() });

  const save = useMutation({ mutationFn: put, onSuccess: (d) => { clearDrafts(); done("Conferência salva")(d); }, onError: fail });
  const finalize = useMutation({
    mutationFn: async (force) => {
      if (dirty) { qc.setQueryData(key, await put()); clearDrafts(); }
      return api(`/conferences/${conf.id}/finalize${force ? "?force=true" : ""}`, { method: "POST" });
    },
    onSuccess: (d) => { setPendingCard(false); done("Conferência finalizada")(d); },
    onError: (e) => (e.status === 409 ? setPendingCard(true) : fail(e)),
  });
  const reopen = useMutation({ mutationFn: () => api(`/conferences/${conf.id}/reopen`, { method: "POST" }), onSuccess: done("Conferência reaberta"), onError: fail });
  const del = useMutation({
    mutationFn: () => api(`/conferences/${conf.id}`, { method: "DELETE" }),
    onSuccess: () => { invalidate(); toast.success("Conferência excluída"); nav("/conferences", { replace: true }); },
    onError: fail,
  });
  return { save, finalize, reopen, del, invalidate, qc };
}

export default function useConferenceEditor(conf) {
  const [drafts, setDrafts] = useState({});
  const [revenue, setRevenue] = useState(numStr(conf.revenue));
  const [mode, setMode] = useState(conf.purchase_mode);
  const [pendingCard, setPendingCard] = useState(false);
  const dirty = Object.keys(drafts).length > 0 || parseNum(revenue) !== (conf.revenue || 0) || mode !== conf.purchase_mode;
  const payload = () => ({ revenue: parseNum(revenue), purchase_mode: mode, items: Object.entries(drafts).map(toPayloadItem) });
  const clearDrafts = () => setDrafts({});
  const m = useConferenceMutations(conf, payload, dirty, clearDrafts, setPendingCard);

  const isCounted = useCallback((it) => (drafts[it.product_id]?.counted !== undefined ? drafts[it.product_id].counted : it.counted), [drafts]);
  const pending = useMemo(() => conf.items.filter((i) => !isCounted(i)), [conf.items, isCounted]);
  const setItem = (pid, f, v) => setDrafts((d) => ({ ...d, [pid]: { ...d[pid], [f]: v, ...(f === "final_qty" && { counted: true }) } }));
  const onEntries = (pid, data, kind) => {
    m.qc.setQueryData(["conference", conf.id], data);
    setDrafts((d) => withoutFields(d, pid, kind === "counts" ? ["final_qty", "counted"] : ["purchases_qty"]));
    ["conferences", "summary", "history"].forEach((k) => m.qc.invalidateQueries({ queryKey: [k] }));
  };
  return { drafts, revenue, setRevenue, mode, setMode, dirty, pendingCard, setPendingCard, isCounted, pending, setItem, onEntries, ...m };
}
